/**
 * Session state — DESIGN.md §13.2.
 *
 * TanStack Query owns server state, including "who am I". Keeping the session
 * in the query cache rather than a separate store means a 401 anywhere in the
 * app invalidates one key and the whole shell re-renders as signed out — no
 * two sources of truth to drift apart.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { LoginRequest, Me } from '@slate/shared';
import { ApiError, http } from './api';

export const meQueryKey = ['auth', 'me'] as const;

export function useMeQuery(): UseQueryResult<Me | null, Error> {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await http.get<Me>('/auth/me');
      } catch (error) {
        // Not signed in is a *state*, not a failure: returning null keeps the
        // login screen out of the error boundary.
        if (error instanceof ApiError && error.isAuthError) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isAuthError) && failureCount < 2,
  });
}

interface AuthContextValue {
  me: Me | null;
  isLoading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const query = useMeQuery();
  const queryClient = useQueryClient();
  const me = query.data ?? null;

  return (
    <AuthContext.Provider
      value={{
        me,
        isLoading: query.isLoading,
        isAdmin: me?.role === 'admin',
        refresh: async () => {
          await queryClient.invalidateQueries({ queryKey: meQueryKey });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** The signed-in user, for components that only render behind a guard. */
export function useCurrentUser(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useCurrentUser used outside an authenticated route');
  return me;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequest) => http.post<Me>('/auth/login', input),
    onSuccess: (me) => {
      queryClient.setQueryData(meQueryKey, me);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<void>('/auth/logout'),
    onSuccess: () => {
      // Signed out *first*, then drop everything else. Note the deliberate
      // avoidance of `queryClient.clear()`: clearing the cache detaches the
      // live `useMeQuery` observer from its query, so the subsequent write is
      // never delivered and the shell stays rendered as signed in.
      queryClient.setQueryData(meQueryKey, null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; password: string; name?: string }) =>
      http.post<Me>('/auth/invite/accept', input),
    onSuccess: (me) => queryClient.setQueryData(meQueryKey, me),
  });
}
